'use client';

import { FC, useEffect, useState, useRef } from 'react';
import { CommentEditor } from '@/components/organisms/CommentEditor';
import {
  CommentThread,
  CommentThreadNode,
  toThreadNode,
} from '@/components/organisms/CommentThread';
import FormSelect from '@/components/molecules/FormSelect/FormSelect';
import { DropdownMenuOptionType } from '@/components/molecules/DropdownMenu/DropdownMenu';
import { DropdownMenuItemVariants } from '@/components/molecules/DropdownMenuItem/DropdownMenuItem';
import { useRouter } from 'next/navigation';
import Badge, { BadgeVariants } from '@/components/molecules/Badge';
// import { CommentButton } from '@/components/molecules/ThreadActionButton/CommentButton';
// import { VoteButton } from '@/components/molecules/VoteButton';
// import SocialIconGroup from '@/components/organisms/SocialIconGroup';
import { getUserAuthHeader } from '@/lib/schema/user/client-fetch';
import { AFTER_LOGIN_REDIRECT_KEY } from '@/lib/constants';
import { showToast } from '../../../../lib/toast';

/* ---------- FastAPI payload -------------------------------------- */
export interface CommentData {
  _id: string;
  content: string;
  createdAt: string;
  // user?: { avatar?: string; username: string; authorId: string };
  upvoteCount?: number;
  downvoteCount?: number;
  replyCount?: number;
  authorId: string;
  replies?: CommentData[];
  userVoteKind?: 'up' | 'down' | null;
}

/* ---------- props ------------------------------------------------- */
interface ArticleCommentsProps {
  postId: string;
  upvotes: number;
  downvotes: number;
  numberOfComments: number;
}

type Sort = 'new' | 'popular';
const SELECT: DropdownMenuOptionType[] = [
  { text: 'New', value: 'new' },
  { text: 'Popular', value: 'popular' },
];

const ArticleComments: FC<ArticleCommentsProps> = ({
  postId,
  // upvotes,
  // downvotes,
  numberOfComments,
}) => {
  const router = useRouter();
  const [threads, setThreads] = useState<CommentThreadNode[]>([]);
  const [sort, setSort] = useState<Sort>('popular');
  const [newRoot, setNewRoot] = useState('');
  const [editorKey, setEditorKey] = useState(0);

  // Add state for highlighted comment
  const [highlightedCommentId, setHighlightedCommentId] = useState<
    string | null
  >(null);

  // Pagination state
  const [page, setPage] = useState(0);
  const [hasMore, setHasMore] = useState(true);
  const [loading, setLoading] = useState(false);
  const COMMENTS_PER_PAGE = 10;

  // Ref for tracking new comments to scroll to
  const latestCommentRef = useRef<string | null>(null);
  const commentEditorRef = useRef<HTMLDivElement>(null);

  // Add a state to track reported comment IDs
  const [reportedCommentIds, setReportedCommentIds] = useState<Set<string>>(
    new Set()
  );

  // Function to handle redirection to login
  const redirectToLogin = () => {
    // Store the full URL path including query parameters
    const currentPath = window.location.pathname + window.location.search;

    // Use the application's redirect constant
    sessionStorage.setItem(AFTER_LOGIN_REDIRECT_KEY, currentPath);

    // Navigate to the login page
    router.push('/login');
  };
  /* -------- reply handler with auth check ----------------------- */
  async function handleReply(parentId: string, body: string) {
    try {
      // Get auth header
      const authHeader = await getUserAuthHeader();

      if (!authHeader) {
        showToast('Error loading comments', { type: 'error' });
        redirectToLogin();
        return;
      }

      const response = await fetch('/api/community/comments', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: authHeader,
        },
        body: JSON.stringify({
          postId,
          parentId,
          content: body,
        }),
      });

      if (response.status === 401) {
        showToast('Error loading comments', { type: 'error' });
        redirectToLogin();
        return;
      }

      if (!response.ok) {
        showToast('Error loading comments', { type: 'error' });
        return;
      }

      const created = await response.json();
      latestCommentRef.current = created._id; // Store the ID of the new comment

      /* clear that branch so its <CommentThread> fetches fresh data */
      const refresh = (n: CommentThreadNode): CommentThreadNode =>
        n.comment.id === parentId
          ? { ...n, replies: [], remainingReplies: n.remainingReplies + 1 }
          : { ...n, replies: n.replies.map(refresh) };
      setThreads((t) => t.map(refresh));
    } catch (error) {
      showToast('Error loading comments', { type: 'error' });
    }
  }
  /* -------- fetch top‑level comments ----------------------------- */
  const load = async (isLoadMore = false) => {
    if (!postId || (loading && isLoadMore)) return;

    try {
      setLoading(true);
      const skip = isLoadMore ? page * COMMENTS_PER_PAGE : 0;
      const sortField = sort === 'new' ? 'createdAt' : 'upvoteCount';

      // Get auth header
      const authHeader = await getUserAuthHeader();

      const res = await fetch(
        `/api/community/comments/${postId}?sort=${sortField}&skip=${skip}&limit=${COMMENTS_PER_PAGE}`,
        {
          headers: authHeader ? { Authorization: authHeader } : {},
        }
      );

      const json = await res.json();
      const newComments = (json.content as CommentData[]).map((c) =>
        toThreadNode(c, postId, handleReply, sortField)
      );

      // Mark any reported comments in the new batch
      const markReportedComments = (
        comment: CommentThreadNode
      ): CommentThreadNode => {
        // Check if this comment is reported
        if (reportedCommentIds.has(comment.comment.id)) {
          return { ...comment, isReported: true };
        }

        // Check replies recursively
        if (comment.replies.length > 0) {
          return {
            ...comment,
            replies: comment.replies.map(markReportedComments),
          };
        }

        return comment;
      };

      const markedComments = newComments.map(markReportedComments);

      if (isLoadMore) {
        // Append new comments to existing ones, ensuring no duplicates
        setThreads((prev) => {
          // Get existing comment IDs
          const existingIds = new Set(prev.map((thread) => thread.comment.id));

          // Filter out duplicates from new comments
          const uniqueNewComments = markedComments.filter(
            (comment) => !existingIds.has(comment.comment.id)
          );

          // Return combined array with no duplicates
          return [...prev, ...uniqueNewComments];
        });

        // Increment page for next load
        setPage((prev) => prev + 1);
      } else {
        // Replace all comments (initial load or sort change)
        // Ensure uniqueness even when replacing all
        const uniqueComments = markedComments.filter(
          (comment, index, self) =>
            self.findIndex((c) => c.comment.id === comment.comment.id) === index
        );
        setThreads(uniqueComments);
        setPage(1); // Reset to page 1 (0-indexed, so next load will be page 1*10 = skip 10)
      }

      // Check if we have more comments to load
      setHasMore(
        json.content.length === COMMENTS_PER_PAGE &&
          json.total > skip + markedComments.length
      );
    } catch (error) {
      showToast('Error loading comments', { type: 'error' });
    } finally {
      setLoading(false);
    }
  };

  // Initial load
  useEffect(() => {
    load(false); // Reset and load first page
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [postId, sort]);

  // Effect to scroll to newly added comment
  useEffect(() => {
    if (latestCommentRef.current) {
      // Find the comment element by its ID
      const commentElements = document.querySelectorAll(
        `[data-comment-id="${latestCommentRef.current}"]`
      );
      if (commentElements.length > 0) {
        // Scroll to the newly added comment
        setTimeout(() => {
          commentElements[0].scrollIntoView({
            behavior: 'smooth',
            block: 'center',
          });
          latestCommentRef.current = null;
        }, 100); // Small delay to ensure DOM is updated
      }
    }
  }, [threads]); // Trigger when threads change

  // Effect to scroll to comment ID in URL hash
  useEffect(() => {
    const scrollToHash = () => {
      // Check if we have threads loaded and a hash in the URL
      if (threads.length > 0 && typeof window !== 'undefined') {
        const { hash } = window.location;
        // The format is #comment-id or just the raw id
        let commentId = '';

        if (hash.startsWith('#comment-')) {
          commentId = hash.substring('#comment-'.length);
        } else if (hash.startsWith('#')) {
          commentId = hash.substring(1); // Remove the # prefix
        }

        if (commentId) {
          // Find the comment element by its ID
          const commentElements = document.querySelectorAll(
            `[data-comment-id="${commentId}"]`
          );

          if (commentElements.length > 0) {
            // Set the highlighted comment
            setHighlightedCommentId(commentId);

            // Scroll to the comment with smooth behavior
            setTimeout(() => {
              commentElements[0].scrollIntoView({
                behavior: 'smooth',
                block: 'center',
              });

              // Remove the hash from the URL after scrolling
              // Using replaceState to modify URL without adding to history
              window.history.replaceState(
                null,
                document.title,
                window.location.pathname + window.location.search
              );

              // Remove the highlight after a few seconds
              setTimeout(() => {
                setHighlightedCommentId(null);
              }, 3000); // Clear highlight after 3 seconds
            }, 100); // Small delay to ensure DOM is updated
          }
        }
      }
    };

    scrollToHash();
  }, [threads]); // Trigger when threads change

  /* -------- post new top‑level comment with auth check ------------- */
  const postRoot = async () => {
    if (!newRoot.trim()) return;

    // Save content before clearing the input
    const content = newRoot.trim();

    // Clear input and force editor remount by incrementing key
    setNewRoot('');
    setEditorKey((prev) => prev + 1);

    try {
      // Get auth header
      const authHeader = await getUserAuthHeader();

      if (!authHeader) {
        showToast('Error loading comments', { type: 'error' });
        redirectToLogin();
        return;
      }

      const response = await fetch('/api/community/comments', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: authHeader,
        },
        body: JSON.stringify({
          postId,
          parentId: null,
          content,
        }),
      });

      if (response.status === 401) {
        showToast('Error loading comments', { type: 'error' });
        redirectToLogin();
        return;
      }

      if (!response.ok) {
        showToast('Error loading comments', { type: 'error' });
        return;
      }

      const created = await response.json();

      // Store ID for scrolling - will be used after load completes
      latestCommentRef.current = created._id;

      // Always switch to 'new' sort after posting a comment
      // This ensures the new comment is visible at the top
      setSort('new');

      // Reset pagination
      setPage(0);

      // Explicitly load comments after state updates
      await load(false);
    } catch (error) {
      showToast('Error loading comments', { type: 'error' });
    }
  };

  // Function to scroll to comment editor
  // const scrollToCommentEditor = () => {
  //   if (commentEditorRef.current) {
  //     commentEditorRef.current.scrollIntoView({
  //       behavior: 'smooth',
  //       block: 'center',
  //     });
  //   }
  // };

  // Effect to listen for comment reported events
  useEffect(() => {
    const handleCommentReported = (
      event: CustomEvent<{ commentId: string }>
    ) => {
      const { commentId } = event.detail;

      // Update the set of reported comment IDs
      setReportedCommentIds((prev) => {
        const prevArray = Array.from(prev);
        return new Set([...prevArray, commentId]);
      });

      // Update the threads to mark the comment as reported
      setThreads((prevThreads) => {
        // Create a function to recursively mark the comment as reported
        const markReported = (node: CommentThreadNode): CommentThreadNode => {
          if (node.comment.id === commentId) {
            return { ...node, isReported: true };
          }

          // Check replies recursively
          if (node.replies.length > 0) {
            return {
              ...node,
              replies: node.replies.map(markReported),
            };
          }

          return node;
        };

        // Apply the function to all threads
        return prevThreads.map(markReported);
      });
    };

    // Add event listener
    window.addEventListener(
      'commentReported',
      handleCommentReported as EventListener
    );

    // Remove event listener on cleanup
    return () => {
      window.removeEventListener(
        'commentReported',
        handleCommentReported as EventListener
      );
    };
  }, []); // Empty dependency array because we don't want to re-add the listener

  /* -------- UI ---------------------------------------------------- */
  return (
    <div className="flex flex-col gap-m ml:gap-xl">
      {/* <div className="flex flex-col gap-xl ml:flex-row ml:justify-between">
        <div className="flex gap-m ml:gap-xl">
          <VoteButton
            type="upvote"
            voted={false}
            voteCount={upvotes}
            onClick={() => {}}
          />
          <VoteButton
            type="downvote"
            voted={false}
            voteCount={downvotes}
            onClick={() => {}}
          />
          <div className="hidden ml:block">
            <CommentButton
              count={numberOfComments}
              onClick={scrollToCommentEditor}
            />
          </div>
        </div>
        <div className="flex flex-col gap-xs ml:flex-row ml:items-center">
          <p className="text-body1Regular">Share this article</p>
          <div className="flex gap-xs">
            <SocialIconGroup keyPrefix="news-article-comments" />
          </div>
        </div>
      </div> */}
      <hr className="h-px bg-neutral-400" />
      <div className="flex gap-s">
        <h4 className="text-h4SemiBold">Comments</h4>
        <Badge
          variant={BadgeVariants.Blue}
          label={numberOfComments.toString()}
        />
      </div>
      <div ref={commentEditorRef}>
        <CommentEditor
          value={newRoot}
          onChange={setNewRoot}
          onSubmit={postRoot}
          key={editorKey}
        />
      </div>

      <FormSelect
        options={SELECT}
        value={[sort]}
        variant={DropdownMenuItemVariants.SingleSelect}
        onChange={([v]) => setSort(v as Sort)}
      />

      {threads.map((t) => (
        <CommentThread
          key={t.comment.id}
          {...t}
          sort={sort}
          isHighlighted={t.comment.id === highlightedCommentId}
        />
      ))}

      {/* Load more comments button */}
      {hasMore && (
        <div className="flex justify-center">
          <button
            className="rounded-md px-4 py-2 font-medium text-blue-medium transition-colors hover:bg-blue-light"
            onClick={() => load(true)}
            disabled={loading}
          >
            {loading ? 'Loading...' : 'Load More Comments'}
          </button>
        </div>
      )}
    </div>
  );
};

export { ArticleComments };
